import type { ImageComponent } from "@2u-landing-pages/types";

export interface ImageTrackingMeta {
  imageUrl: string;
  imageName: string;
  imageFileName: string;
  component: ImageComponent;
  loadingStrategy: "eager" | "lazy";
  imgElement: HTMLImageElement | null;
  loadError?: boolean;
  failedSrc?: string;
  pageTemplate?: string;
}

// Keeps the most recent LCP URL updated via PerformanceObserver for SPA navigations
// that happen after initial page load (when getEntriesByType would be stale).
let lcpImageUrl: string | null = null;
let lcpValue: number | null = null;
let lcpObserverInitialized = false;

function initLcpObserver(): void {
  if (typeof window === "undefined" || lcpObserverInitialized) return;
  lcpObserverInitialized = true;
  try {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const last = entries[entries.length - 1] as { url?: string; startTime?: number };
      if (last?.url) lcpImageUrl = last.url;
      if (typeof last?.startTime === "number") lcpValue = last.startTime;
    });
    // buffered: true replays past entries but the callback fires asynchronously,
    // so we can't rely on lcpImageUrl being set synchronously after this call.
    observer.observe({ type: "largest-contentful-paint", buffered: true });
  } catch {
    // PerformanceObserver for LCP not supported in this environment
  }
}

// Synchronous LCP URL lookup. The browser records LCP entries in the performance
// buffer before JS hydration runs, so getEntriesByType is populated by the time
// any onLoad handler fires — avoiding the async race condition with the observer.
function getCurrentLcpUrl(): string | null {
  if (lcpImageUrl !== null) return lcpImageUrl;
  try {
    const entries = performance.getEntriesByType("largest-contentful-paint");
    if (entries.length === 0) return null;
    const last = entries[entries.length - 1] as { url?: string };
    return last?.url ?? null;
  } catch {
    return null;
  }
}

function getCurrentLcpValue(): number | null {
  if (lcpValue !== null) return lcpValue;
  try {
    const entries = performance.getEntriesByType("largest-contentful-paint");
    if (entries.length === 0) return null;
    const last = entries[entries.length - 1] as { startTime?: number };
    return typeof last?.startTime === "number" ? last.startTime : null;
  } catch {
    return null;
  }
}

function parseUrlParam(url: string, param: string): string | null {
  try {
    const questionIdx = url.indexOf("?");
    if (questionIdx === -1) return null;
    return new URLSearchParams(url.slice(questionIdx)).get(param);
  } catch {
    return null;
  }
}

export function trackImageLoad(meta: ImageTrackingMeta): void {
  if (typeof window === "undefined" || !window.newrelic?.addPageAction) return;

  const {
    imageUrl,
    imageName,
    imageFileName,
    component,
    imgElement,
    loadError = false,
    loadingStrategy,
    failedSrc,
    pageTemplate,
  } = meta;

  // Capture all DOM-dependent metrics synchronously at onLoad time.
  // These values change as the page re-flows so they must be read immediately.
  const entries = performance.getEntriesByName(imageUrl, "resource") as PerformanceResourceTiming[];
  const entry = entries[entries.length - 1];

  const loadTime = entry ? Math.round(entry.responseEnd - entry.startTime) : -1;
  const ttfb = entry ? Math.round(entry.responseStart - entry.startTime) : -1;
  const downloadTime = entry ? Math.round(entry.responseEnd - entry.responseStart) : -1;
  const transferSize = entry?.transferSize ?? -1;
  const encodedBodySize = entry?.encodedBodySize ?? -1;
  const decodedBodySize = entry?.decodedBodySize ?? -1;
  const httpStatus =
    typeof (entry as unknown as { responseStatus?: number } | undefined)?.responseStatus === "number"
      ? (entry as unknown as { responseStatus: number }).responseStatus
      : null;

  const displayedWidth = imgElement?.offsetWidth ?? 0;
  const displayedHeight = imgElement?.offsetHeight ?? 0;
  const devicePixelRatio = window.devicePixelRatio ?? 1;
  const viewportWidth = window.innerWidth ?? 0;
  const viewportHeight = window.innerHeight ?? 0;

  const rect = imgElement?.getBoundingClientRect();
  const wasInViewportOnLoad = rect
    ? rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0
    : false;

  const format = parseUrlParam(imageUrl, "fm") ?? "original";
  const requestedWidthRaw = parseUrlParam(imageUrl, "w");
  const requestedHeightRaw = parseUrlParam(imageUrl, "h");
  const qualityRaw = parseUrlParam(imageUrl, "q");
  const requestedWidth = requestedWidthRaw ? Number(requestedWidthRaw) : null;
  const requestedHeight = requestedHeightRaw ? Number(requestedHeightRaw) : null;
  const quality = qualityRaw ? Number(qualityRaw) : null;

  const transferSizeUnavailable = transferSize === 0 && !entry?.nextHopProtocol && loadTime >= 0;
  const cacheHit = transferSize === 0 && !!entry?.nextHopProtocol && loadTime >= 0;

  const effectiveDisplayedWidth = displayedWidth * devicePixelRatio;
  const overFetchRatio =
    requestedWidth && effectiveDisplayedWidth > 0 ? parseFloat((requestedWidth / effectiveDisplayedWidth).toFixed(2)) : null;
  const isSlowLoad = loadTime > 3000;
  const compressionRatio =
    decodedBodySize > 0 ? parseFloat((encodedBodySize / decodedBodySize).toFixed(3)) : null;

  type NavigatorWithConnection = Navigator & {
    connection?: { effectiveType?: string; type?: string };
  };
  const connection = (navigator as NavigatorWithConnection).connection;
  const connectionType = connection?.effectiveType ?? "unknown";
  const networkType = connection?.type ?? "unknown";

  const pageUrl = window.location.pathname;
  const resolvedPageTemplate = pageTemplate ?? pageUrl;

  const fetchPriority =
    typeof (imgElement as unknown as { fetchPriority?: string } | null)?.fetchPriority === "string"
      ? ((imgElement as unknown as { fetchPriority: string }).fetchPriority as "high" | "low" | "auto")
      : null;

  const isSvg = imgElement?.currentSrc?.toLowerCase().includes(".svg") ?? false;

  const wasPreloaded = (() => {
    try {
      const links = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel="preload"][as="image"]'));
      const currentSrc = (imgElement?.currentSrc || imageUrl).split("#")[0];
      return links.some((l) => (l.href || "").split("#")[0] === currentSrc);
    } catch {
      return false;
    }
  })();

  // Wire up the observer for SPA navigations that happen after initial page load.
  initLcpObserver();

  // Defer past the next paint so the browser has time to commit the LCP entry.
  // The LCP PerformanceEntry is recorded after the paint that follows the image
  // load, meaning getEntriesByType("largest-contentful-paint") returns stale or
  // empty results if called synchronously from onLoad.
  //
  // requestAnimationFrame() yields to the next frame boundary; the nested
  // setTimeout(0) then queues a macrotask that runs after the frame is painted,
  // by which time the LCP entry is in the buffer and the buffered
  // PerformanceObserver callback (initLcpObserver) has also fired.
  requestAnimationFrame(() => {
    setTimeout(async () => {
      if (!window.newrelic?.addPageAction) return;

      const lcpUrl = getCurrentLcpUrl();
      const isLcp = lcpUrl !== null && lcpUrl.split("?")[0] === imageUrl.split("?")[0];
      const lcpValueMsRaw = getCurrentLcpValue();
      const lcpValueMs = typeof lcpValueMsRaw === "number" ? Math.round(lcpValueMsRaw) : null;

      const decodeTime = await (async (): Promise<number> => {
        if (loadError) return -1;
        if (!imgElement?.decode) return -1;
        try {
          const start = performance.now();
          await Promise.race([
            imgElement.decode(),
            new Promise<void>((resolve) => {
              setTimeout(resolve, 2000);
            }),
          ]);
          return Math.max(0, Math.round(performance.now() - start));
        } catch {
          return -1;
        }
      })();

      window.newrelic.addPageAction("ImagePerformanceLoad", {
        loadTime,
        ttfb,
        downloadTime,
        transferSize,
        encodedBodySize,
        decodedBodySize,
        httpStatus,
        format,
        requestedWidth,
        requestedHeight,
        quality,
        displayedWidth,
        displayedHeight,
        devicePixelRatio,
        viewportWidth,
        viewportHeight,
        pageUrl,
        pageTemplate: resolvedPageTemplate,
        imageName,
        imageFileName,
        component,
        connectionType,
        networkType,
        loadingStrategy,
        isLcp,
        lcpValue: lcpValueMs,
        wasInViewportOnLoad,
        cacheHit,
        transferSizeUnavailable,
        loadError,
        failedSrc: failedSrc ?? null,
        overFetchRatio,
        isSlowLoad,
        compressionRatio,
        fetchPriority,
        wasPreloaded,
        decodeTime,
        isSvg,
      });
    }, 0);
  });
}
